import { useState, useEffect } from "react";
import { useAuth } from "@/components/auth/AuthProvider";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { LogIn } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "@/components/ui/use-toast";
import { storeRedirectUrl } from "@/lib/redirectUtils";

interface AuthModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  lang?: string;
  redirectCallback?: () => void;
}

export default function AuthModal({ isOpen, onOpenChange, lang = "en", redirectCallback }: AuthModalProps) {
  const { t, i18n } = useTranslation();
  const { signInWithMagicLink, signInWithGoogle } = useAuth();
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  
  // Set the language based on the prop
  useEffect(() => {
    if (lang && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);

  // Store current path for redirection when modal opens
  useEffect(() => {
    if (isOpen) {
      // Store the current URL for redirection after auth
      const currentPath = window.location.pathname;
      if (currentPath && !currentPath.includes('/auth')) {
        storeRedirectUrl(currentPath);
      }
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await signInWithMagicLink(email);
      setMagicLinkSent(true);
      toast({
        title: t("auth_magic_link_sent_title"),
        description: t("auth_magic_link_sent")
      });
      if (redirectCallback) {
        // Close the modal after a short delay to let user see the success message
        setTimeout(() => {
          onOpenChange(false);
          redirectCallback();
        }, 3000);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t("auth_generic_error"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    setIsGoogleLoading(true);
    
    try {
      await signInWithGoogle();
      // No navigate here as OAuth will redirect
      if (redirectCallback) {
        // This will be executed when the user returns
        setTimeout(() => {
          onOpenChange(false);
          redirectCallback();
        }, 500);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t("auth_google_error"));
      setIsGoogleLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center text-xl">
            {t("auth_login_title")}
          </DialogTitle>
        </DialogHeader>
        
        {error && (
          <div className="rounded-md bg-red-50 dark:bg-red-900/30 p-4 text-sm text-red-500 dark:text-red-200">
            {error}
          </div>
        )}
        
        {/* Google Auth Button - Now First and Bigger */}
        <button
          type="button"
          onClick={handleGoogleSignIn}
          disabled={isGoogleLoading}
          className="w-full flex items-center justify-center gap-3 rounded-md border border-gray-300 bg-white dark:bg-gray-800 px-6 py-3 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 shadow-sm"
        >
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            viewBox="0 0 24 24" 
            className="h-5 w-5"
            aria-hidden="true"
          >
            <path 
              fill="#4285F4" 
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" 
            />
            <path 
              fill="#34A853" 
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" 
            />
            <path 
              fill="#FBBC05" 
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" 
            />
            <path 
              fill="#EA4335" 
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" 
            />
          </svg>
          {isGoogleLoading ? t("connecting") : t("sign_in_with_google")}
        </button>
        
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <Separator className="w-full" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">
              {t("or_continue_with")}
            </span>
          </div>
        </div>
        
        {magicLinkSent ? (
          <div className="rounded-md bg-green-50 dark:bg-green-900/30 p-4 text-sm text-green-500 dark:text-green-200">
            {t("auth_magic_link_sent")}
          </div>
        ) : (
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="space-y-2">
              <label htmlFor="auth-modal-email" className="text-sm font-medium leading-none">
                {t("email")}
              </label>
              <input
                id="auth-modal-email"
                type="email"
                placeholder={t("email_placeholder")}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            
            <button
              type="submit"
              className="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50"
              disabled={isLoading}
            >
              {isLoading ? t("sending") : t("send_magic_link")}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
